import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Users, Trash2, Plus, Pencil, CalendarDays, UserCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "wouter";

interface BlockManagementProps {
  courseId: number;
  courseCapacity?: number;
  onOpenSessions?: (blockId: number) => void;
}

export function BlockManagement({ courseId, courseCapacity, onOpenSessions }: BlockManagementProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState<{ id: number; name: string } | null>(null);
  const [editingBlock, setEditingBlock] = useState<{ id: number; instructorId: number | null } | null>(null);
  const [startDate, setStartDate] = useState("");
  const [instructorId, setInstructorId] = useState<string>("");
  const [maxParticipants, setMaxParticipants] = useState("");

  const utils = trpc.useUtils();

  // Fetch blocks for this course
  const { data: blocks, isLoading: blocksLoading } = trpc.courseBlock.list.useQuery({ courseId });

  // Fetch instructors for dropdown
  const { data: instructors } = trpc.instructor.list.useQuery();

  // Create block mutation
  const createBlock = trpc.courseBlock.create.useMutation({
    onSuccess: () => {
      toast.success("Block erstellt", {
        description: "Der Kursblock wurde erfolgreich erstellt mit allen Sessions.",
      });
      setIsCreateDialogOpen(false);
      setStartDate("");
      setInstructorId("");
      setMaxParticipants("");
      utils.courseBlock.list.invalidate({ courseId });
    },
    onError: (error: any) => {
      toast.error("Fehler", {
        description: error.message,
      });
    },
  });

  // Delete block mutation
  const deleteBlock = trpc.courseBlock.delete.useMutation({
    onSuccess: () => {
      toast.success("Block gelöscht", {
        description: "Der Kursblock wurde erfolgreich gelöscht.",
      });
      setIsDeleteDialogOpen(false);
      setBlockToDelete(null);
      utils.courseBlock.list.invalidate({ courseId });
    },
    onError: (error: any) => {
      toast.error("Fehler", {
        description: error.message,
      });
      setIsDeleteDialogOpen(false);
      setBlockToDelete(null);
    },
  });

  // Update instructor mutation
  const updateInstructor = trpc.courseBlock.updateInstructor.useMutation({
    onSuccess: () => {
      toast.success("Kursleiter aktualisiert", {
        description: "Der Kursleiter wurde erfolgreich geändert.",
      });
      setIsEditDialogOpen(false);
      setEditingBlock(null);
      setInstructorId("");
      utils.courseBlock.list.invalidate({ courseId });
    },
    onError: (error: any) => {
      toast.error("Fehler", {
        description: error.message,
      });
    },
  });

  const handleCreateBlock = () => {
    const finalMaxParticipants = maxParticipants ? parseInt(maxParticipants) : (courseCapacity || 0);
    
    if (!startDate || !instructorId || finalMaxParticipants <= 0) {
      toast.error("Fehler", {
        description: "Bitte alle Felder ausfüllen.",
      });
      return;
    }

    createBlock.mutate({
      courseId,
      startDate,
      instructorId: parseInt(instructorId),
      maxParticipants: finalMaxParticipants,
    });
  };

  const handleDeleteClick = (block: any) => {
    const blockName = `Block ${formatShortDate(block.startDate)}`;
    
    // Check if block has bookings
    if (block.bookedCount > 0) {
      setBlockToDelete({ id: block.id, name: blockName });
      setIsErrorDialogOpen(true);
    } else {
      setBlockToDelete({ id: block.id, name: blockName });
      setIsDeleteDialogOpen(true);
    }
  };

  const confirmDelete = () => {
    if (blockToDelete) {
      deleteBlock.mutate({ id: blockToDelete.id });
    }
  };

  const openEditDialog = (block: any) => {
    setEditingBlock({ id: block.id, instructorId: block.instructorId });
    setInstructorId(block.instructorId?.toString() || "");
    setIsEditDialogOpen(true);
  };

  const handleUpdateInstructor = () => {
    if (!editingBlock || !instructorId) {
      toast.error("Fehler", {
        description: "Bitte einen Kursleiter auswählen.",
      });
      return;
    }

    updateInstructor.mutate({
      id: editingBlock.id,
      instructorId: parseInt(instructorId),
    });
  };

  // Format date for block name (e.g., "22.12.2025")
  const formatBlockDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  // Format short date (e.g., "22.12.")
  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  };

  if (blocksLoading) {
    return <div className="text-muted-foreground">Lade Blocks...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Kursblöcke</h3>
        <Button onClick={() => setIsCreateDialogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Neuer Block
        </Button>
      </div>

      {blocks && blocks.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Noch keine Blocks vorhanden. Erstellen Sie den ersten Block für diesen Kurs.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {blocks?.map((block: any) => (
            <Card key={block.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      Block {formatShortDate(block.startDate)}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                      <span>Kursleiter: {block.instructorName || "Nicht zugewiesen"}</span>
                    </div>
                  </div>
                  <Badge variant={block.status === "active" ? "default" : block.status === "completed" ? "secondary" : "outline"}>
                    {block.status === "active" ? "Aktiv" : block.status === "completed" ? "Abgeschlossen" : block.status === "cancelled" ? "Abgesagt" : "Geplant"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Session Dates */}
                {block.sessionDates && block.sessionDates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {block.sessionDates.map((date: Date | string, idx: number) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {new Date(date).toLocaleDateString("de-DE", { 
                          weekday: "short", 
                          day: "2-digit", 
                          month: "2-digit" 
                        })}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {block.bookedCount}/{block.maxParticipants}
                      </span>
                      <span className="text-muted-foreground">Anmeldungen</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-auto py-1 px-2 text-sm"
                      onClick={() => onOpenSessions?.(block.id)}
                    >
                      <CalendarDays className="h-4 w-4 mr-1" />
                      {block.sessionsCount || 0} Termine
                    </Button>
                  </div>
                  <div className="flex gap-1">
                    <Link href={`/admin/bookings?courseId=${courseId}&blockId=${block.id}`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Anmeldungen für diesen Block anzeigen"
                      >
                        <UserCheck className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(block)}
                      title="Kursleiter bearbeiten"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(block)}
                      disabled={deleteBlock.isPending}
                      title="Block löschen"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie {blockToDelete?.name} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setBlockToDelete(null)}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBlock.isPending ? "Lösche..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Error Dialog - Block has bookings */}
      <AlertDialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Block kann nicht gelöscht werden
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p>
                <strong>{blockToDelete?.name}</strong> hat bereits Anmeldungen und kann daher nicht gelöscht werden.
              </p>
              <p className="mt-2">
                Bitte stornieren Sie zuerst alle Anmeldungen für diesen Block, bevor Sie ihn löschen.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              setIsErrorDialogOpen(false);
              setBlockToDelete(null);
            }}>
              Verstanden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Block Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neuen Block erstellen</DialogTitle>
            <DialogDescription>
              Erstellen Sie einen neuen Kursblock. Die Sessions werden automatisch basierend auf dem Startdatum und der Kurseinstellung generiert.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Startdatum</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instructor">Kursleiter</Label>
              <Select value={instructorId} onValueChange={setInstructorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kursleiter wählen" />
                </SelectTrigger>
                <SelectContent>
                  {instructors?.map((instructor: any) => (
                    <SelectItem key={instructor.id} value={instructor.id.toString()}>
                      {instructor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxParticipants">Maximale Teilnehmerzahl</Label>
              <Input
                id="maxParticipants"
                type="number"
                min="1"
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(e.target.value)}
                placeholder={courseCapacity ? courseCapacity.toString() : ""}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateBlock} disabled={createBlock.isPending}>
              {createBlock.isPending ? "Erstelle..." : "Block erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Block Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block bearbeiten</DialogTitle>
            <DialogDescription>
              Ändern Sie den Kursleiter für diesen Block. Die Änderung wird auch auf alle Sessions des Blocks angewendet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editInstructor">Kursleiter</Label>
              <Select value={instructorId} onValueChange={setInstructorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kursleiter wählen" />
                </SelectTrigger>
                <SelectContent>
                  {instructors?.map((instructor: any) => (
                    <SelectItem key={instructor.id} value={instructor.id.toString()}>
                      {instructor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditDialogOpen(false);
              setEditingBlock(null);
              setInstructorId("");
            }}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdateInstructor} disabled={updateInstructor.isPending}>
              {updateInstructor.isPending ? "Speichere..." : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
